import { Box, Typography } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import { useTranslation } from "react-i18next";
import { Call, CallParticipant } from "../api/graphql";
import { darkPalette, ltrTheme } from "../theme";
import AudioIndicatingAvatar from "./LiveCall/AudioIndicatingAvatar";
import CallControls from "./LiveCall/CallControls";
import ParticipantPlaceholder from "./LiveCall/ParticipantPlaceholder";
import VoiceCallContext from "./LiveCall/VoiceCallContext";

const style: SystemStyleObject<Theme> = {
  px: 2,
  py: 2,
  height: "100%",
  backgroundColor: darkPalette.background.default,
  overflow: "hidden",
  position: "relative",
};

type Props = {
  currentParticipant: Pick<CallParticipant, "externalId">;
  muted: boolean;
  alerts: boolean;
  onToggleAudio: () => void;
  onToggleAlerts: () => void;
  onLeave: () => void;
  leaving?: boolean;
  call: Pick<Call, "features" | "startedAt" | "endedAt" | "unlimited"> & {
    participants: Pick<CallParticipant, "externalId" | "fullName">[];
  };
  missed: boolean;
};

function DialingScreen({
  currentParticipant,
  muted,
  alerts,
  onToggleAlerts,
  onToggleAudio,
  onLeave,
  leaving,
  call,
  missed,
}: Props) {
  const { t } = useTranslation();

  const contact = call.participants.find(
    (e) => e.externalId !== currentParticipant.externalId
  );

  const interval = !call.unlimited
    ? {
        startAt: call.startedAt,
        endAt: call.endedAt,
      }
    : undefined;

  return (
    <Box sx={{ height: 1, width: "100%" }}>
      <Box sx={style}>
        {contact && interval && (
          <>
            <VoiceCallContext
              contact={contact}
              interval={interval}
              features={call.features}
            />
            <ParticipantPlaceholder
              styleOverrides={{ borderRadius: "initial" }}
            >
              <AudioIndicatingAvatar user={contact} stream={undefined} />
              <Typography
                sx={{
                  paddingTop: 1,
                }}
              >
                {missed ? t("Missed") : t("Initializing...")}{" "}
              </Typography>
            </ParticipantPlaceholder>
          </>
        )}
        <Box
          sx={{
            display: "flex",
            width: 1,
            position: "absolute",
            zIndex: "modal",
            left: 0,
            bottom: ltrTheme.spacing(8),
            px: 2,
            height: 0,
          }}
        >
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <CallControls
              muted={muted}
              hidden={false}
              alerts={alerts}
              onToggleAudio={onToggleAudio}
              onToggleAlerts={onToggleAlerts}
              onLeave={onLeave}
              leaveCallLabel={t("End call")}
              leaveCallDisabled={!missed || leaving}
              disabledReason={
                leaving
                  ? t("Leaving the call...")
                  : t("The call is still initializing")
              }
              styleOverrides={{
                transform: "translateY(-50%)",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function BlankDialingScreen() {
  return (
    <Box sx={{ height: 1, width: "100%" }}>
      <Box sx={style} />
    </Box>
  );
}

export default DialingScreen;
